import { useInfiniteSites } from "../../features/projects/hooks/useSites";
import { useInfiniteStakeholders } from "../../features/projects/hooks/useStakeholders";
import { useFilterContext } from "../context";
import { FilterItem } from "../context/FilterContext";

export const useActiveFilterLabels = () => {
  const {
    tempFilters,
    quickFilters,
    options: filterOptions,
  } = useFilterContext();

  const { stakeholders } = useInfiniteStakeholders({
    search: "",
    region: quickFilters.region.length > 0 ? quickFilters.region?.map(r => r.value) : null,
  });

  const normalizedRegion = tempFilters.region?.length
    ? tempFilters.region.map(r => Number(r.value))
    : null;

  const { sites } = useInfiniteSites("", normalizedRegion, null);

  const extractValue = (val: any): string => {
    if (typeof val === 'object' && val !== null && 'value' in val) {
      return String(val.value);
    }
    return String(val);
  };

  const getLabelFromOptions = (key: string, filterItem: FilterItem | any): string => {
    const value = extractValue(filterItem);
    
    const raw = (filterOptions as Record<string, any>)[key];
    const opts: any[] = Array.isArray(raw)
      ? raw
      : Array.isArray(raw?.rows)
        ? raw.rows
        : [];
    
    const match = opts.find((o: any) => String(o.id) === String(value));
    const siteMatch = sites.find((o: any) => String(o.id) === String(value));
    const stakeholderMatch = stakeholders.find((s: any) => String(s.user_id) === String(value));
    
    switch (key) {
      case "programme":
      case "network":
      case "worktype-group":
      case "workstream":
        return match?.description ?? String(value);
      case "site":
        return filterItem?.label ?? siteMatch?.description ?? String(value);
      case "stakeholder":
        return filterItem?.label ?? stakeholderMatch?.full_name ?? String(value);
      default:
        return match?.description ?? String(value);
    }
  };

  const filterKeyLabels: Record<string, string> = {
    site: "Site",
    programme: "Programme",
    region: "Network",
    workstream: "Workstream",
    "worktype-group": "WorkType Group",
    projectYear: "Reporting Year",
    stakeholder: "Stakeholder",
  };

  const activeFilterLabels = Object.entries(quickFilters)
    .filter(([_, values]) => (values as unknown[]).length > 0)
    .map(([key, values]) => {
      const mappedValues = (values as unknown[]).map((val) =>
        getLabelFromOptions(key, val),
      );
      const keyLabel = filterKeyLabels[key] ?? key;
      return `${keyLabel}: ${mappedValues.join(", ")}`;
    });

  const hasActiveFilters = activeFilterLabels.length > 0;
  return {
    hasActiveFilters,
    activeFilterLabels,
  };
};
